import { Injectable, inject, signal, computed, effect } from '@angular/core';
import {
  Auth,
  User,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  UserCredential,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { FunctionsService } from './functions.service';

/**
 * Authentication Service
 *
 * Provides Firebase Authentication with reactive signals
 * Supports email/password authentication and user profile management
 * Includes cross-site authentication via URL token parameter
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth = inject(Auth);
  private router = inject(Router);
  private functionsService = inject(FunctionsService);

  // Reactive state
  private currentUserSignal = signal<User | null>(null);
  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly userEmail = computed(() => this.currentUser()?.email ?? null);
  readonly userId = computed(() => this.currentUser()?.uid ?? null);

  constructor() {
    // Listen to auth state changes
    onAuthStateChanged(this.auth, (user) => {
      this.currentUserSignal.set(user);
    });

    // Check for cross-site authentication token in URL
    this.handleCrossSiteAuth();
  }

  /**
   * Handle cross-site authentication from URL parameter
   * Called during service initialization
   */
  private async handleCrossSiteAuth(): Promise<void> {
    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const authToken = urlParams.get('authToken');

      if (!authToken) {
        return; // No token in URL
      }

      console.log('[AuthService] Cross-site auth token detected, attempting sign in...');

      // Remove token from URL immediately (for security)
      const url = new URL(window.location.href);
      url.searchParams.delete('authToken');
      window.history.replaceState({}, '', url.toString());

      // Exchange ID token for custom token via cloud function
      const result = await this.functionsService.call<{
        customToken: string;
        uid: string;
        email: string;
      }>('exchange_token_for_custom', { idToken: authToken });

      console.log('[AuthService] Custom token received, signing in...');

      // Sign in with custom token
      await signInWithCustomToken(this.auth, result.customToken);

      console.log('✅ [AuthService] Cross-site authentication successful:', result.email);

    } catch (error) {
      console.error('❌ [AuthService] Cross-site authentication failed:', error);
      // Fail silently - user can still use the app and login manually
    }
  }

  /**
   * Sign in with email and password
   */
  async login(email: string, password: string): Promise<User> {
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      return credential.user;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Create new user with email and password
   */
  async register(email: string, password: string, displayName?: string): Promise<User> {
    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Update profile if display name provided
      if (displayName && credential.user) {
        await updateProfile(credential.user, { displayName });
      }

      return credential.user;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out current user
   */
  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
      // Don't navigate - user stays on current page (plans)
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get current user's ID token
   * Used for authenticating cloud function calls
   */
  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken();
  }

  /**
   * Force refresh ID token
   */
  async refreshToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken(true);
  }

  /**
   * Handle Firebase auth errors and convert to user-friendly messages
   */
  private handleAuthError(error: any): Error {
    let message = 'An authentication error occurred';

    switch (error.code) {
      case 'auth/user-not-found':
        message = 'No user found with this email address';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password';
        break;
      case 'auth/email-already-in-use':
        message = 'This email address is already in use';
        break;
      case 'auth/weak-password':
        message = 'Password is too weak. Please use a stronger password';
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address';
        break;
      case 'auth/too-many-requests':
        message = 'Too many failed attempts. Please try again later';
        break;
      case 'auth/network-request-failed':
        message = 'Network error. Please check your connection';
        break;
      default:
        message = error.message || message;
    }

    return new Error(message);
  }
}
